import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, SceneHead, routesFor, type Lang } from "../primitives";

const t = {
  es: {
    aria: "Recursos",
    label: "Recursos",
    title: "Antes de contratar, entiende qué necesitas.",
    read: "Leer el artículo",
    readShort: "Leer",
    featured: {
      key: "libraryTechnicalPrioritization",
      label: "SEO técnico · qué revisar primero",
      title: "Priorización técnica SEO: no todo error importa igual",
      summary:
        "Una auditoría técnica puede arrojar cientos de hallazgos, pero no todos afectan al negocio por igual. Priorizar por impacto en rastreo, indexación y renderizado —cruzado con el tipo de página y el esfuerzo de corrección— evita gastar semanas en errores irrelevantes.",
    },
    secondary: [
      {
        key: "libraryAuditVsStrategy",
        title: "Auditoría SEO o estrategia SEO: qué hacer primero",
        summary:
          "Una auditoría responde qué está fallando en el sitio; una estrategia, qué oportunidad de búsqueda vale la pena perseguir. Cómo decidir por dónde empezar según la situación del negocio.",
      },
      {
        key: "libraryAmazonSeoVsAds",
        title: "Amazon SEO vs Amazon Ads: dónde entra lo orgánico",
        summary:
          "Cómo repartir prioridades entre visibilidad orgánica e inversión publicitaria cuando ambas compiten por el mismo presupuesto.",
      },
    ],
    moreA: "También puedes revisar la",
    moreLib: "biblioteca completa",
    moreB: "y las",
    moreTools: "herramientas gratuitas",
  },
  en: {
    aria: "Resources",
    label: "Resources",
    title: "Before hiring, understand what you need.",
    read: "Read the article",
    readShort: "Read",
    featured: {
      key: "libraryTechnicalPrioritization",
      label: "Technical SEO · what to check first",
      title: "Technical SEO prioritization: not every error matters equally",
      summary:
        "A technical audit can surface hundreds of findings, but they do not all affect the business equally. Prioritizing by impact on crawling, indexing and rendering —cross-checked with page type and fix effort— avoids spending weeks on irrelevant errors.",
    },
    secondary: [
      {
        key: "libraryAuditVsStrategy",
        title: "SEO audit or SEO strategy: what to do first",
        summary:
          "An audit answers what is failing on the site; a strategy answers which search opportunity is worth pursuing. How to decide where to start based on the business situation.",
      },
      {
        key: "libraryAmazonSeoVsAds",
        title: "Amazon SEO vs Amazon Ads: where organic fits",
        summary:
          "How to split priorities between organic visibility and ad spend when both compete for the same budget.",
      },
    ],
    moreA: "You can also browse the",
    moreLib: "full library",
    moreB: "and the",
    moreTools: "free tools",
  },
} as const;

export const SceneResources = ({ language = "es" as Lang }: { language?: Lang }) => {
  const c = t[language];
  const routes = routesFor(language) as unknown as Record<string, string>;

  return (
    <section
      className="v3-section bg-[color:var(--v3-mineral)]"
      aria-label={c.aria}
      id="recursos"
    >
      <div className="v3-shell">
        <SceneHead index="07" label={c.label} title={c.title} />

        <div className="mt-12 grid gap-10 lg:mt-16 lg:grid-cols-12 lg:gap-16">
          <Reveal className="lg:col-span-7">
            <article className="bg-white p-8 lg:p-10">
              <p className="v3-micro">{c.featured.label}</p>
              <h3
                className="v3-display mt-4"
                style={{ fontSize: "clamp(1.35rem, 2.2vw, 1.95rem)", fontWeight: 700 }}
              >
                {c.featured.title}
              </h3>
              <p className="v3-body mt-5">{c.featured.summary}</p>
              <Link to={routes[c.featured.key]} className="v3-inline-link mt-7">
                {c.read}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </article>
          </Reveal>

          <div className="lg:col-span-5">
            {c.secondary.map((r, i) => (
              <Reveal key={r.key} delay={0.05 * i}>
                <article className="border-t border-[color:var(--v3-line)] py-7">
                  <h3 className="text-[1.05rem] font-semibold leading-snug">{r.title}</h3>
                  <p className="mt-3 text-[0.93rem] leading-relaxed text-[color:var(--v3-ink-soft)]">
                    {r.summary}
                  </p>
                  <Link to={routes[r.key]} className="v3-inline-link mt-4">
                    {c.readShort}
                    <ArrowRight className="h-4 w-4" aria-hidden />
                  </Link>
                </article>
              </Reveal>
            ))}

            <div className="mt-6 border-t border-[color:var(--v3-line)] pt-6">
              <p className="text-[0.93rem] text-[color:var(--v3-ink-soft)]">
                {c.moreA}{" "}
                <Link
                  to={routes.library}
                  className="font-medium underline underline-offset-4"
                  style={{ color: "var(--v3-blue)" }}
                >
                  {c.moreLib}
                </Link>{" "}
                {c.moreB}{" "}
                <Link
                  to={routes.seoTools}
                  className="font-medium underline underline-offset-4"
                  style={{ color: "var(--v3-blue)" }}
                >
                  {c.moreTools}
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
